import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { getHardcodedCatalogCourse, getHardcodedCatalogCourses } from "@/lib/data/public-courses";
import { CourseLikeScreenshot } from "@/components/catalog/course-page/CourseLikeScreenshot";
import { getHeroHighlights } from "@/lib/catalog/course-page";
import { getCourseDetailsApiV1CoursesCourseIdGet } from "@/src/api/sdk.gen";
import { useCoursePrices } from "@/lib/catalog/use-course-prices";
import { offerPlanKeyForCatalogSlug } from "@/lib/payments/offer-plan";

export default function CatalogCoursePage() {
  const { courseUuid } = useParams<{ courseUuid: string }>();
  const [course, setCourse] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const hardcoded = courseUuid ? getHardcodedCatalogCourse(courseUuid) : null;
  const allHardcoded = getHardcodedCatalogCourses();
  const prices = useCoursePrices(allHardcoded.map((c) => c.course_id));

  useEffect(() => {
    if (hardcoded || !courseUuid) {
      setLoading(false);
      return;
    }
    // Fetch from public course API endpoint
    getCourseDetailsApiV1CoursesCourseIdGet({
      path: { course_id: courseUuid as any },
    }).then(({ data, error }: { data: any; error: any }) => {
      if (data && !error) {
        setCourse(data);
      }
      setLoading(false);
    }).catch(() => {
      setLoading(false);
    });
  }, [courseUuid, hardcoded]);

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-950 text-white">Loading...</div>;
  }

  if (hardcoded) {
    // Prices from the plans checkout charges, matched to the page's level names by position.
    const price = prices?.[hardcoded.course_id];
    const pending = prices == null;
    const normalizedLevelPricing = hardcoded.level_pricing?.map((row, i) => ({
      level: row.level,
      price: pending ? "…" : price?.levels[i]?.priceText ?? "Not on sale yet",
    }));
    const normalizedLifetimeText = pending
      ? "…"
      : price?.totalText
        ? `${price.totalText} for all levels`
        : "Not on sale yet";

    const moreCourses = allHardcoded
      .filter((c) => c.slug !== hardcoded.slug)
      .map((c) => ({
        slug: c.slug,
        title: c.course_name,
        badge: c.badge,
        priceText: prices?.[c.course_id]?.perLevelText ? `${prices[c.course_id].perLevelText} per level` : null,
      }));
    return (
      <CourseLikeScreenshot
        course={{
          title: hardcoded.course_name,
          description: hardcoded.description ?? hardcoded.tagline ?? null,
          providerName: "InaraX",
          badgeText: hardcoded.badge ?? null,
          lastUpdatedText: null,
          lifetimeAccessText: normalizedLifetimeText,
          levelPricing: normalizedLevelPricing,
          mockAverageRating: 4.6,
          mockRatingCount: 189,
          mockLearnerCount: 2340,
          heroHighlights: getHeroHighlights(hardcoded.slug, hardcoded.course_name),
          learnItems: hardcoded.curriculum_highlights ?? [],
          levelModules: hardcoded.level_modules,
          requirements: hardcoded.requirements,
          longDescription: hardcoded.long_description ?? null,
          outcomes: hardcoded.outcomes,
          moreCourses,
          catalogSlug: hardcoded.slug,
          offerPlanKey: offerPlanKeyForCatalogSlug(hardcoded.slug),
        }}
      />
    );
  }

  if (!course) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-950 text-white">Course not found</div>;
  }

  return (
    <CourseLikeScreenshot
      course={{
        title: course.title,
        description: course.description ?? null,
        providerName: "InaraX",
        badgeText: null,
        lastUpdatedText: null,
        lifetimeAccessText: "Per level (lifetime access)",
        mockAverageRating: 4.5,
        mockRatingCount: 96,
        mockLearnerCount: 1280,
        heroHighlights: getHeroHighlights(courseUuid || "", course.title),
        learnItems:
          course.modules?.length
            ? course.modules.slice(0, 10).map((m: any) => m.title)
            : ["Structured lessons", "Knowledge checks", "Skills for real work scenarios"],
        catalogSlug: courseUuid || "",
        offerPlanKey: offerPlanKeyForCatalogSlug(courseUuid || ""),
      }}
    />
  );
}
